import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Briefcase, Check, Contact, Download, ExternalLink, ListChecks, Mail, Pencil, Plus, RotateCcw, Trash2, Upload, UserPlus, UserX, X } from "lucide-react";
import { api, telecharger, jeton } from "./api";
import { Section, Tableau, Cellule, Eyebrow, ATonAttention, EnTeteMenu, Puces, Badge, GRAVITES } from "./ui";
import { useConfirmation } from "./Confirmation";
import HorairesPartages from "./HorairesPartages";
import RemplacerProf from "./RemplacerProf";
import { CartesProfs } from "./DashboardPlus";
import { useChantiers } from "../lib/useChantiers";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"];
const moisLisible = (m) => { const [a, mm] = m.split("-").map(Number); return `${MOIS[mm - 1]} ${a}`; };
// « d'octobre 2026 », « de novembre 2026 ».
const deMois = (m) => { const t = moisLisible(m); return /^[aeiou]/.test(t) ? `d'${t}` : `de ${t}`; };
const moisCourant = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };
const decaler = (m, n) => {
  const [a, mm] = m.split("-").map(Number);
  const d = new Date(a, mm - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const jour = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const argent = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
// « −20.– », « +20.– », « 12.50 » : les montants d'une régularisation (comme le serveur).
const court = (n, signe = false) => {
  const x = Math.round(Number(n || 0) * 100) / 100;
  const a = Math.abs(x);
  const corps = Number.isInteger(a) ? `${a}.–` : a.toFixed(2);
  return x < 0 ? `−${corps}` : (signe && x > 0 ? `+${corps}` : corps);
};
// « de novembre », « d'octobre » (sans l'année) : « Reporter sur le décompte de novembre ».
const deMoisSeul = (m) => { const t = MOIS[Number(String(m).slice(5, 7)) - 1] || ""; return /^[aeiou]/.test(t) ? `d'${t}` : `de ${t}`; };
// Une ligne « Régularisation octobre — … » : la différence d'un mois déjà validé, reportée ici.
const estRegul = (l) => l.type === "régularisation";
const duree = (min) => `${Math.floor((min || 0) / 60)}h${String((min || 0) % 60).padStart(2, "0")}`;
/* Les couleurs des décomptes, comme celles des factures (école, 30.10.2026) : bleu = à
   l'école de valider, orange = le prof n'a pas encore envoyé un mois terminé, vert =
   validé, gris = rien à faire pour l'instant. `ordre` : les décomptes à traiter d'abord. */
const ETATS = {
  a_valider: { label: "Envoyé · à valider", filtre: "À valider", ton: "bleu", ordre: 0 },
  pas_envoye: { label: "Pas encore envoyé", filtre: "Pas encore envoyés", ton: "ambre", ordre: 1 },
  en_cours: { label: "Mois en cours", filtre: "Mois en cours", ton: "neutre", ordre: 2 },
  a_venir: { label: "À venir", filtre: "À venir", ton: "neutre", ordre: 2 },
  // Un mois d'avant l'application des profs : décompté à la main, le prof n'a rien à envoyer.
  main: { label: "Fait à la main", filtre: "Faits à la main", ton: "neutre", ordre: 2 },
  vide: { label: "Aucun cours", filtre: "Aucun cours", ton: "neutre", ordre: 3 },
  valide: { label: "Validé", filtre: "Validés", ton: "vert", ordre: 4 },
};
const etatDecompte = (x, mois, horsApp) => {
  if (x.statut === "valide") return "valide";
  if (x.statut === "confirme") return "a_valider";
  if (horsApp) return "main";
  if (!x.seances) return "vide";
  if (mois > moisCourant()) return "a_venir";
  return mois === moisCourant() ? "en_cours" : "pas_envoye";
};
const jourCourt = (iso) => jour(iso).slice(0, 5);
// La petite ligne sous le badge : quand, et où en est le prof.
const detailEtat = (cle, x) => {
  const rappel = x.relance_le ? ` · rappel envoyé le ${jourCourt(x.relance_le)}` : "";
  if (cle === "valide") return x.valide_le ? `le ${jourCourt(x.valide_le)}` : "";
  if (cle === "a_valider") return x.confirme_le ? `envoyé le ${jourCourt(x.confirme_le)}` : "";
  if (cle === "pas_envoye") return (x.a_cocher ? `${x.a_cocher} cours à cocher` : "tout est coché, reste à l'envoyer") + rappel;
  if (cle === "en_cours") return [x.a_cocher && `${x.a_cocher} à cocher`, x.a_venir && `${x.a_venir} à venir`].filter(Boolean).join(" · ");
  if (cle === "a_venir") return x.a_venir ? `${x.a_venir} cours prévus` : "";
  if (cle === "main") return "ancien système, avant l'app";
  return "";
};
// Où en est le prof dans son application (Jen, 30.10.2026 : « Claudio a confirmé 4 cours
// sur 7 ») : les cours du mois cochés ✓ (vert) et marqués ✗ « pas donné » (orange).
const AVANCEMENT = ["en_cours", "pas_envoye"];
// Une séance sans élève (0.–) : rien à cocher pour le prof, rien à vérifier ici (comme
// profs.sans_eleve côté serveur, tests du 01.11.2026).
const sansEleve = (l) => ["cours", "remplacement"].includes(l.type) && !l.eleves && !l.minutes && !Number(l.montant || 0);
const compteCours = (lignes) => {
  const du = (lignes || []).filter((l) => l.retire_par !== "école" && !sansEleve(l) && !estRegul(l));
  return { cours_du_mois: du.length, cours_confirmes: du.filter((l) => l.confirme && !l.retire).length,
    cours_pas_donnes: du.filter((l) => l.retire_par === "prof").length };
};
const Avancement = ({ x, testid }) => {
  const total = x.cours_du_mois || 0;
  if (!total) return null;
  const ok = x.cours_confirmes || 0;
  const non = x.cours_pas_donnes || 0;
  return (
    <div className="mt-1.5 w-full max-w-[240px]" data-testid={testid}>
      <div className="text-xs whitespace-nowrap">
        <strong className="tabular-nums">{ok} sur {total}</strong> cours confirmé{ok > 1 ? "s" : ""}
        {non > 0 && <span className="text-[var(--muted)]"> · {non} pas donné{non > 1 ? "s" : ""}</span>}
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden mt-1" style={{ background: "#E2E8F0" }} aria-hidden="true">
        <div style={{ width: `${(ok / total) * 100}%`, background: GRAVITES.vert.puce }} />
        <div style={{ width: `${(non / total) * 100}%`, background: GRAVITES.ambre.puce }} />
      </div>
    </div>
  );
};
// Les gestes d'une ligne du tableau : petits, pour garder des lignes basses.
const PETIT_BOUTON = "inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-semibold bg-white hover:bg-[var(--bg-soft)]";
const BadgeDecompte = ({ cle, testid }) => (
  <Badge ton={ETATS[cle].ton} testid={testid}>
    {cle === "valide" ? <Check size={11} /> : <span className="w-1.5 h-1.5 rounded-full" style={{ background: GRAVITES[ETATS[cle].ton].puce }} />}
    {ETATS[cle].label}
  </Badge>
);

/* Le premier mois des heures dans l'application des profs se règle dans Réglages → Saison
   et calendrier (réglage de mise en route, 21.10.2026). Ce lien y mène, à la bonne section. */
const versLeReglage = () => {
  try { sessionStorage.setItem("reglages_groupe", "saison"); sessionStorage.setItem("reglages_ancre", "heures-profs"); } catch { /* rien */ }
  window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "reglages" } }));
};
/* Ce que l'école a à faire côté profs (13.10.2026) : valider les décomptes envoyés, relancer
   ceux qui n'ont pas envoyé le mois fini, trouver un remplaçant. `aller(onglet, mois)`. */
const pointsProfs = (s, aller) => {
  if (!s) return [];
  const p = [];
  const parMois = {};
  (s.a_valider || []).forEach((x) => { (parMois[x.mois] = parMois[x.mois] || []).push(x.prof); });
  Object.entries(parMois).sort().forEach(([m, noms]) => p.push({
    id: `valider-${m}`, ton: "ambre",
    texte: `Décomptes ${deMois(m)} envoyés par ${noms.join(", ")} : à vérifier et valider`,
    onClick: () => aller("decomptes", m),
  }));
  const pasEnvoyes = s.precedent_dans_la_saison ? (s.precedent_totaux?.statuts?.brouillon || 0) : 0;
  if (pasEnvoyes) {
    const noms = (s.par_prof || []).filter((x) => x.precedent && x.precedent.statut === "brouillon" && x.precedent.heures > 0)
      .map((x) => x.prenom);
    p.push({ id: "pas-envoyes", ton: "ambre",
      texte: `${noms.length ? noms.join(", ") : `${pasEnvoyes} prof(s)`} : décompte ${deMois(s.precedent)} pas encore envoyé`,
      onClick: () => aller("decomptes", s.precedent) });
  }
  // Un décompte validé qui n'est plus juste (élève changé de cours « depuis le début »…) :
  // la différence attend « Reporter » ou « Ignorer » sur le décompte (Jen, 02.11.2026).
  (s.regularisations || []).forEach((r) => p.push({
    id: `regul-${r.prof_id}-${r.mois}`, ton: "ambre", texte: r.titre,
    onClick: () => aller("decomptes", r.mois, r.prof_id),
  }));
  const sansRemplacant = s.absences?.sans_remplacant || 0;
  if (sansRemplacant) {
    p.push({ id: "remplacants", ton: "rouge",
      texte: `${sansRemplacant} absence(s) ce mois sans remplaçant désigné`,
      onClick: () => aller("absences") });
  }
  // Vacances avec un remplaçant externe proposé par le prof : l'appeler, puis « Remplaçant
  // confirmé » (Jen, 01.11.2026 ; l'e-mail le demandait sans bouton pour le faire).
  const aConfirmer = s.remplacants_a_confirmer || [];
  if (aConfirmer.length) {
    p.push({ id: "remplacants-a-confirmer", ton: "ambre",
      texte: `Remplaçant externe à confirmer : ${aConfirmer.map((x) => `${x.prof} (${x.du === x.au ? jour(x.du).slice(0, 5)
        : `${jour(x.du).slice(0, 5)} → ${jour(x.au).slice(0, 5)}`}, ${x.remplacant})`).join(" · ")}`,
      onClick: () => aller("absences") });
  }
  return p;
};

const ONGLETS = [
  { id: "decomptes", label: "Décomptes du mois" },
  { id: "absences", label: "Absences et remplacements" },
  { id: "fiches", label: "Fiches des profs" },
  { id: "horaires", label: "Horaires partagés" },
];

/* Une ligne du décompte, telle que l'école peut la retoucher : la retirer (ou la remettre),
   corriger sa durée ou son montant, supprimer une ligne ajoutée. */
const LigneDecompte = ({ l, modifiable, agir, annulerRegul }) => {
  const [edition, setEdition] = useState(false);
  const [montant, setMontant] = useState(String(l.montant ?? ""));
  const [minutes, setMinutes] = useState(String(l.minutes ?? ""));
  const [note, setNote] = useState("");
  if (estRegul(l)) return (
    <>
      <Cellule muet>{""}</Cellule>
      <Cellule fort>
        <span data-testid="profs-ligne-regul">{l.cours}</span>
        <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5 align-middle"
          style={{ background: "#FEF3C7", color: "#92400E" }}>régularisation</span>
        <div className="text-xs font-normal text-[var(--muted)]">{l.note}</div>
        {l.explication && <div className="text-xs font-normal text-[var(--muted)]">Le prof lit : « {l.explication} »</div>}
      </Cellule>
      <Cellule muet>{""}</Cellule>
      <Cellule muet>{""}</Cellule>
      <Cellule>{""}</Cellule>
      <Cellule muet>{""}</Cellule>
      <Cellule droite fort>{argent(l.montant)}</Cellule>
      <Cellule droite>
        {modifiable && l.regularisation?.id && (
          <button className="btn-ghost text-xs" title="Annuler le report" data-testid="profs-regul-annuler"
            onClick={() => annulerRegul(l.regularisation.id)}><RotateCcw size={13} /></button>
        )}
      </Cellule>
    </>
  );
  // Retirée : barrée ; sans élève (0.–, rien à vérifier) : en clair.
  const raye = l.retire ? { textDecoration: "line-through", opacity: 0.5 } : sansEleve(l) ? { opacity: 0.5 } : undefined;
  const texteNote = l.retire ? `retiré par ${l.retire_par === "école" ? "l'école" : "le prof"}`
    : l.ajout_prof ? String(l.note || "").replace(/^ajouté par le prof( · )?/, "") : (l.note || "");
  return (
    <>
      <Cellule muet>{jour(l.date)}</Cellule>
      <Cellule fort>
        <span style={raye}>{l.cours}</span>
        {l.remplace && <span className="text-xs font-normal"> (remplace {l.remplace})</span>}
        {l.ajout_prof && (
          <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5 align-middle" data-testid="profs-ligne-ajout-prof"
            style={{ background: "#EDE9FE", color: "#6D28D9" }}>ajouté par le prof</span>
        )}
        {!l.retire && !sansEleve(l) && String(l.note || "").includes("à vérifier") && (
          <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5 align-middle"
            style={{ background: "#FEF3C7", color: "#92400E" }}>horaire à vérifier</span>
        )}
        <div className="text-xs font-normal text-[var(--muted)]">
          {/* « ajouté par le prof » : déjà dit par la pastille violette (il s'affichait deux fois). */}
          {texteNote}
          {!l.retire && l.confirme && <span style={{ color: "#16A34A" }}>{texteNote ? " · " : ""}✓ confirmé par le prof</span>}
        </div>
        {edition && (
          <div className="flex flex-wrap items-end gap-2 mt-2 font-normal">
            <label className="text-xs">Durée (min)<input className="input-field w-20 mt-1" value={minutes} onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ""))} /></label>
            <label className="text-xs">Montant CHF<input className="input-field w-24 mt-1" value={montant} onChange={(e) => setMontant(e.target.value.replace(/[^\d.]/g, ""))} /></label>
            <label className="text-xs flex-1 min-w-[140px]">Pourquoi<input className="input-field w-full mt-1" value={note} placeholder="ex. cours raccourci" onChange={(e) => setNote(e.target.value)} /></label>
            <button className="btn-primary text-xs" data-testid="profs-ligne-enregistrer" onClick={async () => {
              const ok = await agir({ action: "corriger", cle: l.cle,
                minutes: minutes !== String(l.minutes) ? Number(minutes) : null,
                montant: montant !== String(l.montant) ? Number(montant) : null, note });
              if (ok) setEdition(false);
            }}><Check size={13} /> Enregistrer</button>
            <button className="btn-ghost text-xs" onClick={() => setEdition(false)}>Annuler</button>
          </div>
        )}
      </Cellule>
      <Cellule muet>{l.debut ? `${l.debut}–${l.fin}` : ""}</Cellule>
      <Cellule muet>{l.minutes ? duree(l.minutes) : ""}</Cellule>
      <Cellule>{["cours", "remplacement", "ajout"].includes(l.type) ? l.eleves : ""}</Cellule>
      <Cellule muet>{l.montant_convenu ? "convenu" : l.tarif ? `${l.tarif}.– ${l.tarif_par === "cours" ? "/cours" : "/h"}` : ""}</Cellule>
      <Cellule droite fort>{argent(l.retire ? 0 : l.montant)}</Cellule>
      <Cellule droite>
        {modifiable && (
          <span className="inline-flex gap-1">
            {l.retire ? (
              <button className="btn-ghost text-xs" title="Remettre" data-testid="profs-ligne-remettre"
                onClick={() => agir({ action: "remettre", cle: l.cle })}><RotateCcw size={13} /></button>
            ) : (
              <>
                <button className="btn-ghost text-xs" title="Corriger la durée ou le montant" data-testid="profs-ligne-corriger"
                  onClick={() => setEdition(!edition)}><Pencil size={13} /></button>
                <button className="btn-ghost text-xs" title="Retirer (pas payé)" data-testid="profs-ligne-retirer"
                  onClick={() => agir({ action: "retirer", cle: l.cle })}><X size={13} /></button>
              </>
            )}
            {String(l.cle).startsWith("ajout-") && (
              <button className="btn-ghost text-xs" title="Supprimer la ligne ajoutée"
                onClick={() => agir({ action: "supprimer", cle: l.cle })}><Trash2 size={13} /></button>
            )}
          </span>
        )}
      </Cellule>
    </>
  );
};

/* Un décompte validé n'est plus juste : une donnée a changé depuis (élève changé de cours
   « depuis le début », inscription corrigée). Le mois reste tel quel ; la différence se
   reporte d'un tap sur le prochain décompte du prof, ou s'ignore avec la raison (Jen,
   02.11.2026). Dessous, ce qui a déjà été reporté ou ignoré pour ce mois. */
const BlocRegularisation = ({ detail, mois, decider, annuler, ouvrir, corriger }) => {
  const [ignorer, setIgnorer] = useState(false);
  const [motif, setMotif] = useState("");
  const r = detail.regularisation;
  const historique = detail.regularisations || [];
  if (!r && !historique.length) return null;
  return (
    <div className="space-y-2">
      {r && (
        <div className="rounded-2xl px-4 py-4 space-y-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
          data-testid="profs-regularisation">
          <div>
            <div className="font-semibold" style={{ color: "#92400E" }} data-testid="profs-regul-titre">Depuis la validation : {court(r.montant, true)}</div>
            <ul className="text-sm mt-1 space-y-0.5">
              {r.resume.map((g, k) => <li key={k} data-testid="profs-regul-ligne">{g.texte}</li>)}
            </ul>
          </div>
          <p className="text-xs text-[var(--muted)]">
            Le décompte {deMoisSeul(mois)} reste tel qu'il a été validé.
            {(r.pour_le_prof || []).length > 0 && <> {detail.prof.prenom} lira : « {r.pour_le_prof.join(" ")} »</>}
          </p>
          {ignorer ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs flex-1 min-w-[200px]">Pourquoi ignorer ?
                <input className="input-field w-full mt-1" value={motif} autoFocus placeholder="ex. déjà réglé avec le prof"
                  onChange={(e) => setMotif(e.target.value)} data-testid="profs-regul-motif" />
              </label>
              <button className="btn-primary text-xs" disabled={!motif.trim()} data-testid="profs-regul-ignorer-ok"
                onClick={async () => { if (await decider("ignorer", motif.trim())) { setIgnorer(false); setMotif(""); } }}>
                Ignorer la différence
              </button>
              <button className="btn-ghost text-xs" onClick={() => setIgnorer(false)}>Annuler</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary" data-testid="profs-regul-reporter" disabled={!r.mois_cible}
                onClick={() => decider("reporter")}>
                <ArrowRight size={14} /> Reporter sur le décompte {deMoisSeul(r.mois_cible)}
              </button>
              {/* Jen, 02.11.2026 : « l'école doit choisir si elle veut modifier octobre ou
                  reporter sur novembre — ils ne l'ont peut-être pas encore payé ». */}
              <button className="btn-ghost" data-testid="profs-regul-corriger" onClick={corriger}>
                <RotateCcw size={14} /> Corriger {deMoisSeul(mois).replace(/^(de |d')/, "")} (pas encore payé)
              </button>
              <button className="btn-ghost" data-testid="profs-regul-ignorer" onClick={() => setIgnorer(true)}>Ignorer</button>
            </div>
          )}
        </div>
      )}
      {historique.length > 0 && (
        <div className="text-xs text-[var(--muted)] space-y-1 px-1" data-testid="profs-regul-historique">
          {historique.map((h) => (
            <div key={h.id}>
              {h.statut === "reportee"
                ? <>{court(h.montant, true)} reporté sur le décompte {deMoisSeul(h.mois_cible)}</>
                : <>{court(h.montant, true)} ignoré{h.motif ? ` — ${h.motif}` : ""}</>}
              {" "}· le {jourCourt(h.le)}{h.par ? `, ${h.par}` : ""}
              {" "}
              {h.statut === "reportee"
                ? <button type="button" className="underline" onClick={() => ouvrir(h.mois_cible)}>voir {moisLisible(h.mois_cible)}</button>
                : <button type="button" className="underline" onClick={() => annuler(h.id)} data-testid="profs-regul-reprendre">reprendre</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Ajouter une ligne : un cours privé (tarif du site), ou une heure à part (répétition,
   remplacement non prévu…) au barème ou à un montant convenu. */
const AjouterLigne = ({ mois, coursPrives, agir }) => {
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ date: `${mois}-01`, libelle: "", debut: "", fin: "", eleves: "", montant: "", cours_prive: "" });
  if (!ouvert) {
    return <button className="btn-ghost" onClick={() => setOuvert(true)} data-testid="profs-ajouter-ligne"><Plus size={14} /> Ajouter une ligne</button>;
  }
  const champ = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  return (
    <div className="bg-white rounded-2xl border p-4 flex flex-wrap items-end gap-2" style={{ borderColor: "var(--border)" }}>
      <label className="text-xs">Date<input type="date" className="input-field mt-1" min={`${mois}-01`} max={`${mois}-31`} {...champ("date")} /></label>
      <label className="text-xs">Type
        <select className="input-field mt-1" {...champ("cours_prive")}>
          <option value="">Heure à part</option>
          {coursPrives.map((c) => <option key={c.id} value={c.id}>{c.label} — {c.montant}.–</option>)}
        </select>
      </label>
      {!f.cours_prive && (
        <>
          <label className="text-xs flex-1 min-w-[160px]">Quoi<input className="input-field w-full mt-1" placeholder="répétition spectacle…" {...champ("libelle")} /></label>
          <label className="text-xs">De<input type="time" className="input-field mt-1" {...champ("debut")} /></label>
          <label className="text-xs">À<input type="time" className="input-field mt-1" {...champ("fin")} /></label>
          <label className="text-xs">Élèves<input className="input-field w-16 mt-1" {...champ("eleves")} /></label>
        </>
      )}
      <label className="text-xs">Montant convenu<input className="input-field w-24 mt-1" placeholder="barème" {...champ("montant")} /></label>
      <button className="btn-primary text-xs" data-testid="profs-ajout-valider" onClick={async () => {
        const ok = await agir({ action: "ajouter", ajout: { date: f.date, libelle: f.libelle, debut: f.debut, fin: f.fin,
          eleves: Number(f.eleves || 0), montant: Number(f.montant || 0), cours_prive: f.cours_prive } });
        if (ok) setOuvert(false);
      }}><Check size={13} /> Ajouter</button>
      <button className="btn-ghost text-xs" onClick={() => setOuvert(false)}>Annuler</button>
    </div>
  );
};

/* Un prof malade appelle l'école (il ne l'annonce plus dans son app) : l'école note
   l'absence ici, puis désigne un remplaçant dans le tableau. */
const NoterAbsence = ({ profs, essayer, charger }) => {
  const vide = { prof_id: "", du: "", au: "", type: "maladie", motif: "" };
  const [f, setF] = useState(null);
  if (!f) return (
    <button className="btn-primary text-sm" onClick={() => setF(vide)} data-testid="profs-noter-absence">
      <Plus size={14} /> Noter une absence
    </button>
  );
  return (
    <div className="bg-white rounded-2xl border p-4 flex flex-wrap items-end gap-2 w-full" style={{ borderColor: "var(--border)" }}
      data-testid="profs-absence-form">
      <select className="input-field" value={f.prof_id} onChange={(e) => setF({ ...f, prof_id: e.target.value })}>
        <option value="">— prof —</option>
        {profs.filter((p) => p.actif !== false).map((p) => <option key={p.id} value={p.id}>{p.prenom}</option>)}
      </select>
      <label className="text-xs text-[var(--muted)]">Du<input type="date" className="input-field block" value={f.du} onChange={(e) => setF({ ...f, du: e.target.value })} /></label>
      <label className="text-xs text-[var(--muted)]">Au (inclus)<input type="date" className="input-field block" value={f.au} min={f.du} onChange={(e) => setF({ ...f, au: e.target.value })} /></label>
      <select className="input-field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
        <option value="maladie">Maladie</option>
        <option value="vacances">Vacances</option>
        <option value="autre">Autre</option>
      </select>
      <input className="input-field" placeholder="Motif (facultatif)" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} />
      <button className="btn-primary text-sm" disabled={!f.prof_id || !f.du}
        onClick={() => essayer(async () => {
          await api("/profs/absences", { method: "POST", body: JSON.stringify(f) });
          setF(null); charger();
        }, "Absence notée : désigne un remplaçant dans le tableau.")}><Check size={14} /> Enregistrer</button>
      <button className="btn-ghost text-sm" onClick={() => setF(null)}>Annuler</button>
    </div>
  );
};

/* Les profs : décomptes du mois (vérifier, corriger, valider), absences et remplaçants,
   fiches (la seule source des noms complets, aussi pour les attestations), et le tableau
   des horaires partagés de la comédie musicale. */
/* Le mois d'un coup d'œil (école, 30.10.2026) : une barre aux couleurs des badges, le
   total du mois, et des filtres qui servent aussi de légende. */
const ResumeDecomptes = ({ decomptes, mois, filtre, setFiltre }) => {
  const liste = (decomptes?.decomptes || []).map((x) => etatDecompte(x, mois, decomptes.hors_app));
  if (!liste.length) return null;
  const nb = {};
  liste.forEach((k) => { nb[k] = (nb[k] || 0) + 1; });
  const cles = Object.keys(ETATS).filter((k) => nb[k]);
  const total = decomptes.decomptes.reduce((t, x) => t + Number(x.total || 0), 0);
  const heures = decomptes.decomptes.reduce((t, x) => t + Number(x.heures || 0), 0);
  // Les cours que les profs ont déjà confirmés, tant que des décomptes restent à envoyer.
  const enCours = decomptes.decomptes.filter((x, i) => AVANCEMENT.includes(liste[i]));
  const coursTotal = enCours.reduce((t, x) => t + (x.cours_du_mois || 0), 0);
  const coursOk = enCours.reduce((t, x) => t + (x.cours_confirmes || 0), 0);
  const puce = (k, label, n) => {
    const g = k === "tous" ? GRAVITES.neutre : GRAVITES[ETATS[k].ton];
    const actif = filtre === k;
    return (
      <button key={k} type="button" onClick={() => setFiltre(actif && k !== "tous" ? "tous" : k)} data-testid={`profs-filtre-${k}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors"
        style={{ background: actif ? g.fond : "#fff", borderColor: actif ? g.puce : "var(--border)", color: actif ? g.texte : "var(--text)" }}>
        {k !== "tous" && <span className="w-2 h-2 rounded-full" style={{ background: g.puce }} />}
        {label} <span className="font-semibold tabular-nums">{n}</span>
      </button>
    );
  };
  return (
    <div className="bg-white rounded-2xl border p-4 mb-4 space-y-3" style={{ borderColor: "var(--border)" }} data-testid="profs-resume">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
        <span><strong>{nb.valide || 0} sur {liste.length}</strong> décomptes validés
          {nb.a_valider ? <> · <strong style={{ color: GRAVITES.bleu.texte }}>{nb.a_valider} à valider</strong></> : null}
          {nb.pas_envoye ? <> · <strong style={{ color: GRAVITES.ambre.texte }}>{nb.pas_envoye} pas encore envoyé{nb.pas_envoye > 1 ? "s" : ""}</strong></> : null}
          {coursTotal > 0 && <span data-testid="profs-resume-cours"> · <strong>{coursOk} sur {coursTotal}</strong> cours confirmés par les profs</span>}
        </span>
        <span className="text-[var(--muted)]">{heures.toFixed(2)} h · <strong className="text-[var(--text)]">{argent(total)}</strong> brut au total</span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }} aria-hidden="true">
        {cles.map((k) => <div key={k} style={{ width: `${(nb[k] / liste.length) * 100}%`, background: GRAVITES[ETATS[k].ton].puce }} />)}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="profs-filtres">
        {puce("tous", "Tous", liste.length)}
        {cles.map((k) => puce(k, ETATS[k].filtre, nb[k]))}
      </div>
    </div>
  );
};

/* Le bouton d'un e-mail à l'école (Jen, 01.11.2026 ; backend console/mail.lien_console) :
   « decomptes:2026-10:prof » ouvre le décompte de ce prof pour ce mois (« Vérifier et
   valider »), « absences:PABS-… » ouvre les absences, cette ligne mise en avant. */
const lireCible = (cible) => {
  const [vue, ...reste] = String(cible || "").split(":");
  if (!ONGLETS.some((o) => o.id === vue)) return null;
  if (vue === "decomptes" && /^\d{4}-\d{2}$/.test(reste[0] || "")) return { vue, mois: reste[0], prof: reste.slice(1).join(":") };
  return { vue, ligne: reste.join(":") };
};

export default function Profs({ cible }) {
  const { demander, informer } = useConfirmation();
  const chantiers = useChantiers();
  const [d, setD] = useState(null);
  const [vise] = useState(() => lireCible(cible));
  const [onglet, setOnglet] = useState(vise?.vue || "decomptes");
  // Le mois de l'heure locale (toISOString donnait le mois UTC : le 1er avant 2 h du
  // matin, la page s'ouvrait sur le mois précédent).
  // Le mois ouvert d'office : jusqu'au 10, le mois précédent (les profs envoient leurs
  // heures en fin de mois, l'école valide au début du suivant) ; ensuite, le mois en cours
  // (école, 30.10.2026 : « ça reste sur le mois en cours ? quand passe-t-on à novembre ? »).
  const [mois, setMois] = useState(() => {
    if (vise?.mois) return vise.mois;
    const n = new Date();
    const courant = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
    return n.getDate() <= 10 ? decaler(courant, -1) : courant;
  });
  const [decomptes, setDecomptes] = useState(null);
  const [detail, setDetail] = useState(null);
  const [horaires, setHoraires] = useState(null);
  const [nouveau, setNouveau] = useState(null);
  // Remplacer un prof : l'assistant, et les remplacements prévus ou faits.
  const [remplacer, setRemplacer] = useState(false);
  const [remplacements, setRemplacements] = useState([]);
  const chargerRemplacements = useCallback(() => {
    api("/remplacements").then((r) => setRemplacements(r.remplacements || [])).catch(() => setRemplacements([]));
  }, []);
  const [message, setMessage] = useState("");
  const [validation, setValidation] = useState(false);
  const [erreur, setErreur] = useState("");
  // Le téléphone refusé : le message sous SON champ (en haut de page, on ne le voyait pas).
  const [telErreur, setTelErreur] = useState({});
  // « À ton attention » : les mêmes chiffres que la section Profs du tableau de bord
  // (/stats/profs), relus après chaque changement de décompte.
  const [suivi, setSuivi] = useState(null);
  // Décomptes du mois : le filtre par couleur (« tous », « a_valider »…), remis à zéro
  // quand on change de mois.
  const [filtre, setFiltre] = useState("tous");
  useEffect(() => setFiltre("tous"), [mois]);

  const charger = useCallback(() => {
    api("/profs").then(setD).catch((e) => setErreur(e.message));
  }, []);
  const chargerMois = useCallback(() => {
    setDecomptes(null);
    api(`/profs/decomptes/${mois}`).then(setDecomptes).catch((e) => setErreur(e.message));
  }, [mois]);
  useEffect(charger, [charger]);
  useEffect(chargerMois, [chargerMois]);
  useEffect(chargerRemplacements, [chargerRemplacements]);
  useEffect(() => { api("/stats/profs").then(setSuivi).catch(() => setSuivi(null)); }, [decomptes]);
  useEffect(() => {
    if (onglet === "horaires" && !horaires) api("/profs/horaires").then(setHoraires).catch((e) => setErreur(e.message));
  }, [onglet, horaires]);

  const essayer = async (fn, succes) => {
    setErreur(""); setMessage("");
    try { await fn(); if (succes) setMessage(succes); return true; } catch (e) { setErreur(e.message); return false; }
  };
  // « Remplaçant confirmé » / « Annuler la confirmation » ; « À ton attention » suit.
  const confirmerRemplacant = (a, confirme) => essayer(async () => {
    const x = await api(`/profs/absences/${a.id}/remplacant-confirme`, { method: "POST", body: JSON.stringify({ confirme }) });
    setMessage(x.message);
    charger();
    api("/stats/profs").then(setSuivi).catch(() => {});
  });
  const enregistrer = (p, champs) => essayer(async () => {
    await api(`/profs/${p.id}`, { method: "PUT", body: JSON.stringify(champs) });
    charger();
  }, `Fiche de ${p.prenom} enregistrée.`);
  const enregistrerTel = async (p, champ) => {
    const v = champ.value.trim();
    if (v === (p.telephone || "")) return setTelErreur((x) => ({ ...x, [p.id]: "" }));
    try {
      await api(`/profs/${p.id}`, { method: "PUT", body: JSON.stringify({ telephone: v }) });
      setTelErreur((x) => ({ ...x, [p.id]: "" }));
      setMessage(`Téléphone de ${p.prenom} enregistré.`);
      charger();
    } catch (e) {
      champ.value = p.telephone || "";
      setTelErreur((x) => ({ ...x, [p.id]: `« ${v} » pas enregistré : ${e.message}` }));
    }
  };
  const ouvrirDetail = (pid) => essayer(async () => setDetail(await api(`/profs/${pid}/decompte/${mois}`)));
  // Le décompte d'un autre mois (« À ton attention », « voir novembre ») : mois et détail ensemble.
  const ouvrirDecompte = (pid, m) => essayer(async () => {
    const x = await api(`/profs/${encodeURIComponent(pid)}/decompte/${m}`);
    setMois(m); setOnglet("decomptes"); setDetail(x);
  });
  // « Reporter » / « Ignorer » la différence d'un mois validé ; le décompte est relu, même
  // si les chiffres ont changé entre-temps (le serveur refuse alors, avec une phrase).
  const deciderRegul = async (action, motif = "") => {
    setErreur(""); setMessage("");
    const pid = detail.prof.id;
    let ok = true;
    try {
      const r = await api(`/profs/${pid}/decompte/${mois}/regularisation`, { method: "POST",
        body: JSON.stringify({ action, motif, empreinte: detail.regularisation?.empreinte || "" }) });
      setMessage(r.message);
    } catch (e) { setErreur(e.message); ok = false; }
    try { setDetail(await api(`/profs/${pid}/decompte/${mois}`)); } catch { /* le message suffit */ }
    chargerMois();
    api("/stats/profs").then(setSuivi).catch(() => {});
    return ok;
  };
  /* Corriger le mois validé lui-même, quand le prof n'a pas encore été payé : il est
     rouvert et recalculé avec les données d'aujourd'hui, puis l'école le revalide. */
  const corrigerMoisRegul = async () => {
    const pid = detail.prof.id;
    if (!(await demander({
      titre: `Corriger le décompte ${deMois(mois)} ?`,
      texte: `À choisir seulement si ${detail.prof.prenom} n'a pas encore été payé pour ce mois.`,
      points: [`Le décompte ${deMois(mois)} est rouvert et recalculé avec les données d'aujourd'hui (${court(detail.regularisation?.montant || 0, true)}).`,
        "Vérifie-le, puis clique « Valider » à nouveau.",
        "Si le prof est déjà payé, choisis plutôt « Reporter sur le décompte » du mois suivant."],
      valider: "Rouvrir et corriger",
      annuler: "Garder tel quel",
    }))) return;
    setErreur(""); setMessage("");
    try {
      await api(`/profs/${pid}/decompte/${mois}/rouvrir`, { method: "POST" });
      setMessage(`Décompte ${deMois(mois)} rouvert et recalculé : vérifie-le, puis « Valider ».`);
    } catch (e) { setErreur(e.message); }
    try { setDetail(await api(`/profs/${pid}/decompte/${mois}`)); } catch { /* le message suffit */ }
    chargerMois();
    api("/stats/profs").then(setSuivi).catch(() => {});
  };
  const annulerRegul = async (id) => {
    if (!(await demander({
      titre: "Annuler ce report ?",
      texte: "La ligne quitte ce décompte. La différence revient sur le décompte validé : tu pourras la reporter à nouveau, ou l'ignorer.",
      valider: "Annuler le report",
    }))) return;
    const pid = detail.prof.id;
    essayer(async () => {
      const r = await api(`/profs/regularisations/${encodeURIComponent(id)}/annuler`, { method: "POST" });
      setDetail(await api(`/profs/${pid}/decompte/${mois}`));
      setMessage(r.message); chargerMois();
      api("/stats/profs").then(setSuivi).catch(() => {});
    });
  };
  // Ouvert depuis l'e-mail « Heures confirmées » : le décompte du prof, prêt à valider.
  useEffect(() => {
    if (vise?.prof && vise.mois) ouvrirDetail(encodeURIComponent(vise.prof));
  }, [vise]); // eslint-disable-line react-hooks/exhaustive-deps
  const agir = (corps) => essayer(async () => {
    setDetail(await api(`/profs/${detail.prof.id}/decompte/${mois}/ligne`, { method: "POST", body: JSON.stringify(corps) }));
    chargerMois();
  });
  const valider = async () => {
    if (detail.statut !== "confirme" && !(await demander({
      titre: "Valider sans l'envoi du prof ?",
      texte: decomptes?.hors_app
        ? `${moisLisible(mois)} est avant l'application : ces heures ont été faites à la main. Valider quand même ce décompte (il apparaîtra dans l'onglet Paie de ${detail.prof.prenom}) ?`
        : `${detail.prof.prenom} n'a pas encore envoyé ses heures ${deMois(mois)}. Valider quand même ?`,
      valider: "Valider quand même",
    }))) return;
    // Séances d'un cours partagé sans horaire au tableau : le serveur refuse sans accord.
    const douteuses = detail.lignes.filter((l) => !l.retire && !sansEleve(l) && String(l.note || "").includes("à vérifier"));
    if (douteuses.length && !(await demander({
      titre: "Horaires partagés à vérifier",
      texte: `${douteuses.length} séance(s) d'un cours partagé n'ont pas d'horaire dans le tableau « Horaires » (${douteuses.map((l) => jour(l.date)).join(", ")}). Elles sont comptées en entier. Valider quand même ?`,
      valider: "Valider tel quel",
    }))) return;
    // Durées annoncées par les profs (horaires partagés) pas encore appliquées ni refusées.
    const attente = detail.propositions_en_attente || 0;
    if (attente && !(await demander({
      titre: "Durées annoncées par les profs",
      texte: `${attente} durée(s) annoncée(s) sur ces cours attendent ta décision (onglet « Horaires partagés » → « Propositions des profs »). Valider quand même ce décompte tel quel ?`,
      valider: "Valider tel quel",
    }))) return;
    // La validation recalcule tout le mois (quelques secondes) : le bouton le dit, puis une
    // fenêtre confirme ce qui a été validé et où le prof le retrouve (école, 09.10.2026 :
    // « rien n'indique le temps que ça prend ou quand c'est fait »).
    const prenom = detail.prof.prenom;
    setValidation(true);
    let d = null;
    const ok = await essayer(async () => {
      d = await api(`/profs/${detail.prof.id}/decompte/${mois}/valider${douteuses.length || attente ? "?forcer=true" : ""}`, { method: "POST" });
      setDetail(null); chargerMois();
    });
    setValidation(false);
    if (ok) {
      await informer({
        titre: `${moisLisible(mois)} validé pour ${prenom}`, ton: "succes",
        texte: `${Number(d?.heures || 0).toFixed(2)} h · CHF ${Number(d?.total || 0).toFixed(2)} brut.`,
        points: [`${prenom} le retrouve tout de suite dans son application : onglet « Paie » (fiche PDF à télécharger) et onglet « Heures », mois ${deMois(mois)} (« Validé par l'école »).`,
          "Une erreur ? « Rouvrir » sur ce décompte, corriger, puis valider à nouveau."],
      });
    }
  };
  // « Relancer » : l'e-mail « Tes heures du mois », tout de suite (école, 30.10.2026).
  const relancer = async (prof) => {
    if (!(await demander({
      titre: `Relancer ${prof.prenom} ?`,
      texte: `${prof.prenom} reçoit tout de suite l'e-mail « Tes heures du mois » ${deMois(mois)} (le même que le rappel automatique du 1er et du 5) : cocher ses cours dans l'application, puis envoyer ses heures.`,
      valider: "Envoyer le rappel",
    }))) return;
    essayer(async () => {
      const r = await api(`/profs/${prof.id}/decompte/${mois}/relancer`, { method: "POST" });
      setMessage(r.message); chargerMois();
    });
  };
  const rouvrir = (pid) => essayer(async () => {
    await api(`/profs/${pid}/decompte/${mois}/rouvrir`, { method: "POST" });
    setDetail(null); chargerMois();
  }, "Décompte rouvert.");
  const apercu = async (pid) => {
    const { jeton: j } = await api(`/profs/${pid}/apercu`, { method: "POST" });
    window.open(`/prof?jeton=${encodeURIComponent(j)}`, "_blank", "noopener");
  };
  const importer = async (evt) => {
    const fichier = evt.target.files?.[0];
    evt.target.value = "";
    if (!fichier) return;
    const corps = new FormData();
    corps.append("fichier", fichier);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/console/profs/horaires/import`,
      { method: "POST", headers: { Authorization: `Bearer ${jeton()}` }, body: corps });
    const r = await res.json().catch(() => ({}));
    if (res.ok) { setMessage(`${r.lignes} dates importées (les autres dates restent inchangées).`); setErreur(""); }
    else setErreur(r.detail || `Erreur ${res.status}`);
    setHoraires(null); chargerMois();
  };

  if (!d) return <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  const bandeaux = (
    <>
      {message && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}>{message}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="profs-erreur">{erreur}</div>}
    </>
  );

  if (detail) {
    const p = detail.prof;
    const modifiable = detail.statut !== "valide";
    const etatDetail = etatDecompte({ ...detail, seances: detail.lignes.filter((l) => !l.retire).length }, mois, decomptes?.hors_app);
    return (
      <div className="space-y-6" data-testid="profs-detail">
        <button className="btn-ghost" onClick={() => { setDetail(null); setMessage(""); setErreur(""); }}><ArrowLeft size={14} /> Retour</button>
        {bandeaux}
        <div>
          <Eyebrow>Décompte · {moisLisible(mois)}</Eyebrow>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <h1 className="font-display text-3xl">{p.nom_complet || p.prenom}</h1>
            <BadgeDecompte cle={etatDetail} testid="profs-detail-statut" />
            <span className="text-xs text-[var(--muted)]">{detailEtat(etatDetail, detail)}</span>
          </div>
          {AVANCEMENT.includes(etatDetail) && <Avancement x={compteCours(detail.lignes)} testid="profs-detail-avancement" />}
          <p className="text-sm mt-1">{detail.heures.toFixed(2)} h · <strong>{argent(detail.total)}</strong> brut</p>
          {modifiable && <p className="text-xs text-[var(--muted)] mt-1">Tu peux retirer une ligne, corriger sa durée ou son montant, ou en ajouter une, avant de valider.</p>}
        </div>
        <BlocRegularisation detail={detail} mois={mois} decider={deciderRegul}
          annuler={annulerRegul} ouvrir={(m) => ouvrirDecompte(p.id, m)} corriger={corrigerMoisRegul} />
        <Tableau
          entetes={["Date", { label: "Cours", large: true }, "Horaire", "Durée", "Élèves", "Tarif", { label: "Montant", droite: true }, ""]}
          lignes={detail.lignes}
          rendu={(l) => <LigneDecompte key={l.cle} l={l} modifiable={modifiable} agir={agir} annulerRegul={annulerRegul} />}
        />
        {modifiable && <AjouterLigne mois={mois} coursPrives={d.cours_prives || []} agir={agir} />}
        <div className="flex flex-wrap gap-2">
          {modifiable ? (
            <button className="btn-primary" onClick={valider} disabled={validation} data-testid="profs-valider">
              <Check size={14} /> {validation ? "Validation en cours…" : "Valider et mettre à disposition du prof"}
            </button>
          ) : (
            <button className="btn-ghost" onClick={() => rouvrir(p.id)}><RotateCcw size={14} /> Rouvrir</button>
          )}
          {etatDetail === "pas_envoye" && p.email && (
            <button className="btn-ghost" onClick={() => relancer(p)} data-testid="profs-detail-relancer"><Mail size={14} /> Relancer {p.prenom}</button>
          )}
          <button className="btn-ghost" onClick={() => telecharger(`/profs/${p.id}/decompte/${mois}/pdf`, `decompte-${p.id}-${mois}.pdf`)}>
            <Download size={14} /> PDF
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="console-profs">
      {/* Pas sur le papier : la feuille des horaires partagés s'imprime seule. */}
      <div className="sans-impression space-y-6">
        <EnTeteMenu ton="profs" Icon={Briefcase} contexte="Espace prof" titre="Profs" testid="profs-entete"
          points={[
            [ListChecks, "Heures et paie", "Le prof coche ses séances, l'école valide ici"],
            [UserX, "Absences", "Et remplaçants"],
            [Contact, "Fiches et horaires", "Coordonnées, horaires partagés"],
          ]} />
        {/* Les chiffres clés, comme sur les autres menus (Jen, 31.10.2026). */}
        <CartesProfs onOuvrir={() => setOnglet("decomptes")} />
        <ATonAttention testid="profs-attention" points={pointsProfs(suivi, (o, m, pid) => {
          setOnglet(o); if (m) setMois(m); setMessage(""); setErreur("");
          if (pid && m) ouvrirDecompte(pid, m);
        })} />
        <Puces testid="profs-onglets" valeur={onglet}
          onChange={(id) => { setOnglet(id); setMessage(""); setErreur(""); }}
          options={ONGLETS.map((o) => ({ valeur: o.id, label: o.label, testid: `profs-onglet-${o.id}` }))} />
        {bandeaux}
      </div>

      {onglet === "decomptes" && (
        <Section titre={`Décomptes ${deMois(mois)}`}
          aide="Chaque prof coche ses cours dans son application et envoie son décompte à la fin du mois ; tu vérifies et tu valides ici. Clique sur un prof pour voir le détail ou corriger une ligne. Les profs reçoivent un rappel automatique le 1er et le 5 du mois."
          action={
            <span className="flex flex-wrap items-center gap-1">
              {/* Changer de mois : les mois voisins écrits en toutes lettres, bien visibles. */}
              <span className="inline-flex items-center rounded-full border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }}
                data-testid="profs-choix-mois">
                <button className="px-3 py-1.5 text-xs font-medium hover:bg-[var(--bg)] inline-flex items-center gap-1"
                  onClick={() => setMois(decaler(mois, -1))} data-testid="profs-mois-precedent">
                  <ArrowLeft size={13} /> {moisLisible(decaler(mois, -1))}
                </button>
                <span className="px-3 py-1.5 text-xs font-semibold text-white" style={{ background: "var(--primary)" }}>{moisLisible(mois)}</span>
                <button className="px-3 py-1.5 text-xs font-medium hover:bg-[var(--bg)] inline-flex items-center gap-1"
                  onClick={() => setMois(decaler(mois, 1))} data-testid="profs-mois-suivant">
                  {moisLisible(decaler(mois, 1))} <ArrowRight size={13} />
                </button>
              </span>
              {/* Les paiements : heures, total brut, IBAN et adresse de chaque prof, plus le
                  détail des séances. Orange = décompte pas encore validé. */}
              <button className="btn-ghost text-xs" data-testid="profs-export-mois"
                onClick={() => essayer(() => telecharger(`/profs/export.xlsx?du=${mois}`, `paiements-profs-${mois}.xlsx`))}>
                <Download size={13} /> Excel du mois
              </button>
              <button className="btn-ghost text-xs" data-testid="profs-export-annee"
                onClick={() => {
                  const an = Number(mois.slice(0, 4)) - (Number(mois.slice(5)) < 8 ? 1 : 0);
                  essayer(() => telecharger(`/profs/export.xlsx?du=${an}-08&au=${mois}`, `paiements-profs-${an}-08-a-${mois}.xlsx`));
                }}>
                <Download size={13} /> Depuis la rentrée
              </button>
            </span>
          } testid="profs-decomptes">
          {decomptes?.hors_app && (
            <div className="rounded-2xl px-4 py-3 text-sm mb-4" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }} data-testid="profs-mois-hors-app">
              <strong>{moisLisible(mois)} : avant l'application.</strong> Ces heures ont été faites à la main (ancien système) :
              les profs ne voient rien à envoyer pour ce mois et ne reçoivent aucun rappel. Les montants ci-dessous ne sont qu'une estimation du planning.
              {" "}<button type="button" className="underline text-[var(--muted)]" onClick={versLeReglage} data-testid="profs-vers-reglage">
                Réglage : Réglages → Saison et calendrier
              </button>
            </div>
          )}
          <ResumeDecomptes decomptes={decomptes} mois={mois} filtre={filtre} setFiltre={setFiltre} />
          <Tableau
            testid="profs-decomptes-tableau"
            entetes={["Prof", { label: "Statut", large: true }, "Heures", { label: "Total brut", droite: true }, ""]}
            lignes={(decomptes?.decomptes || [])
              .map((x) => ({ ...x, id: x.prof.id, etat: etatDecompte(x, mois, decomptes.hors_app) }))
              .filter((x) => filtre === "tous" || x.etat === filtre)
              .sort((a, b) => ETATS[a.etat].ordre - ETATS[b.etat].ordre
                || (a.prof.nom_complet || a.prof.prenom).localeCompare(b.prof.nom_complet || b.prof.prenom, "fr"))}
            vide={!decomptes ? "Calcul…" : filtre === "tous" ? "Aucun prof payé." : "Aucun décompte dans ce filtre."}
            onClic={(x) => ouvrirDetail(x.prof.id)}
            rendu={(x) => (
              <>
                <Cellule fort><span className="whitespace-nowrap">{x.prof.nom_complet || x.prof.prenom}</span></Cellule>
                <Cellule>
                  <BadgeDecompte cle={x.etat} testid={`profs-statut-${x.prof.id}`} />
                  {AVANCEMENT.includes(x.etat) && <Avancement x={x} testid={`profs-avancement-${x.prof.id}`} />}
                  {detailEtat(x.etat, x) && <div className="text-[11px] text-[var(--muted)] mt-1">{detailEtat(x.etat, x)}</div>}
                  {x.a_regulariser != null && (
                    <div className="text-[11px] font-semibold mt-1" style={{ color: GRAVITES.ambre.texte }} data-testid={`profs-a-regulariser-${x.prof.id}`}>
                      {court(x.a_regulariser, true)} à reporter
                    </div>
                  )}
                </Cellule>
                <Cellule muet><span className="whitespace-nowrap">{x.heures.toFixed(2)} h</span></Cellule>
                <Cellule droite fort>
                  <span className="whitespace-nowrap">{argent(x.total)}</span>
                  {/* Pas encore envoyé : le montant n'est que ce que propose le planning. */}
                  {x.statut === "brouillon" && x.seances > 0 && <div className="text-[11px] font-normal text-[var(--muted)]">estimation</div>}
                </Cellule>
                <Cellule droite>
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    {x.etat === "pas_envoye" && x.prof.email && (
                      <button className={PETIT_BOUTON} style={{ borderColor: "var(--border)" }} data-testid={`profs-relancer-${x.prof.id}`}
                        onClick={(e) => { e.stopPropagation(); relancer(x.prof); }}>
                        <Mail size={12} /> Relancer
                      </button>
                    )}
                    {x.etat === "a_valider"
                      ? <span className={`${PETIT_BOUTON} text-white`} style={{ background: "var(--primary)", borderColor: "var(--primary)" }}>Vérifier et valider <ArrowRight size={12} /></span>
                      : <span className={PETIT_BOUTON} style={{ borderColor: "var(--border)" }}>Ouvrir</span>}
                  </span>
                </Cellule>
              </>
            )}
          />
        </Section>
      )}

      {onglet === "absences" && (
        <Section titre="Absences des profs"
          aide="Annoncées depuis l'application prof (vacances 30 jours avant avec un remplaçant externe, autre absence 15 jours avant) ou notées par l'école (« Noter une absence » : un prof malade appelle l'école). Les deux derniers mois restent visibles. Désigne un remplaçant de l'équipe : il est prévenu par e-mail, voit le cours dans son application et il est payé au barème. Un remplaçant externe (proposé par le prof) est payé directement par l'école, sans application : appelle-le, puis « Remplaçant confirmé » — le prof le voit dans son app."
          action={<NoterAbsence profs={d.profs} essayer={essayer} charger={charger} />}
          testid="profs-absences">
          <Tableau
            entetes={["Prof", "Quand", "Motif", "Remplaçant", ""]}
            lignes={d.absences.filter((a) => !a.annulee_le)}
            surligne={vise?.vue === "absences" && vise.ligne ? (a) => a.id === vise.ligne : undefined}
            vide="Aucune absence annoncée."
            rendu={(a) => (
              <>
                <Cellule fort>{a.prof}</Cellule>
                <Cellule muet>
                  {jour(a.du)}{a.au !== a.du ? ` → ${jour(a.au)}` : ""}
                  {/* Pour quel cours (l'absence d'un seul cours ne le disait pas). */}
                  <div className="text-[11px]" data-testid="profs-absence-cours">{a.cours_nom ? `seulement : ${a.cours_nom}` : "tous ses cours"}</div>
                </Cellule>
                <Cellule>
                  <span className="break-words">{a.type}{a.motif ? ` — ${a.motif}` : ""}</span>
                  {a.note_par_ecole && <div className="text-[11px] text-[var(--muted)]">notée par l'école</div>}
                </Cellule>
                <Cellule>
                  {a.remplacant_externe && (
                    <div className="text-xs mb-1" data-testid="profs-remplacant-externe">
                      <strong>{a.remplacant_externe.prenom} {a.remplacant_externe.nom}</strong> (externe)<br />
                      <a href={`tel:${String(a.remplacant_externe.telephone).replace(/[^\d+]/g, "")}`} className="underline">{a.remplacant_externe.telephone}</a>
                      {" · "}<a href={`mailto:${a.remplacant_externe.email}`} className="underline">{a.remplacant_externe.email}</a>
                    </div>
                  )}
                  {/* L'école a appelé le remplaçant externe : « Remplaçant confirmé » (qui, quand) ;
                      le prof le lit dans son app. Un remplaçant de l'équipe désigné : rien à confirmer. */}
                  {a.remplacant_externe && !a.remplacant_id && (a.remplacant_confirme_le ? (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5" data-testid="profs-remplacant-confirme">
                      <Badge ton="vert"><Check size={11} /> Confirmé le {new Date(a.remplacant_confirme_le).toLocaleDateString("fr-CH")}{a.remplacant_confirme_par ? ` · ${a.remplacant_confirme_par}` : ""}</Badge>
                      <button type="button" className="text-[11px] underline underline-offset-2 text-[var(--muted)]" data-testid="profs-remplacant-deconfirmer"
                        onClick={() => confirmerRemplacant(a, false)}>Annuler la confirmation</button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <Badge ton="ambre">À confirmer</Badge>
                      <button type="button" className="btn-primary text-xs" data-testid="profs-remplacant-confirmer"
                        onClick={() => confirmerRemplacant(a, true)}><Check size={12} /> Remplaçant confirmé</button>
                    </div>
                  ))}
                  <select className="input-field" value={a.remplacant_id || ""}
                    onChange={(e) => essayer(async () => {
                      await api(`/profs/absences/${a.id}`, { method: "PUT", body: JSON.stringify({ remplacant_id: e.target.value }) });
                      charger();
                    }, e.target.value ? "Remplaçant désigné et prévenu." : "Remplaçant retiré.")}>
                    <option value="">— personne —</option>
                    {d.profs.filter((p) => p.id !== a.prof_id && p.actif !== false).map((p) => <option key={p.id} value={p.id}>{p.prenom}</option>)}
                  </select>
                </Cellule>
                <Cellule droite>
                  {/* Une absence notée par erreur (ou le prof vient quand même) : elle s'annule ici. */}
                  <button className="btn-ghost text-xs" title="Annuler cette absence" data-testid="profs-absence-annuler"
                    onClick={async () => {
                      if (!(await demander({
                        titre: `Annuler l'absence de ${a.prof} ?`,
                        texte: `${jour(a.du)}${a.au !== a.du ? ` → ${jour(a.au)}` : ""} : ses cours reviennent dans ses heures${a.remplacant_id ? " et son remplaçant est prévenu par e-mail que le remplacement n'a plus lieu" : ""}.`,
                        valider: "Annuler l'absence",
                      }))) return;
                      essayer(async () => {
                        await api(`/profs/absences/${a.id}`, { method: "DELETE" });
                        charger();
                      }, "Absence annulée.");
                    }}><Trash2 size={13} /> Annuler</button>
                </Cellule>
              </>
            )}
          />
        </Section>
      )}

      {remplacer && d && (
        <RemplacerProf profs={d.profs} onFermer={() => setRemplacer(false)}
          onFait={(msg) => { setRemplacer(false); setMessage(msg); charger(); chargerRemplacements(); }} />
      )}
      {onglet === "fiches" && remplacements.filter((r) => r.statut === "prevu" || r.statut === "applique").length > 0 && (
        <div className="rounded-2xl border p-4 bg-white text-sm space-y-2" style={{ borderColor: "var(--border)" }} data-testid="profs-remplacements">
          <div className="font-semibold">Remplacements</div>
          {remplacements.filter((r) => r.statut === "prevu" || r.statut === "applique").slice(0, 8).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2">
              <span><strong>{r.nouveau_prenom}</strong> remplace {r.ancien_prenom} à partir du {jour(r.des_le)} · {(r.cours || []).map((c) => c.nom).join(", ")}</span>
              <span className="text-xs text-[var(--muted)]">{r.statut === "prevu" ? "prévu" : `fait${r.familles_prevenues ? ` · ${r.familles_prevenues} famille(s) prévenue(s)` : ""}`}</span>
              {r.statut === "prevu" && (
                <button className="btn-ghost text-xs" onClick={() => essayer(async () => {
                  await api(`/remplacements/${r.id}`, { method: "DELETE" }); chargerRemplacements(); charger();
                }, "Remplacement annulé.")}>Annuler</button>
              )}
            </div>
          ))}
        </div>
      )}
      {onglet === "fiches" && (
        <Section titre="Fiches des profs"
          aide="Le seul endroit où se tiennent les profs : le nom complet sert aux attestations d'assurance et aux décomptes, l'e-mail à la connexion à l'application (un prof = une adresse). Le téléphone se règle ici ou par le prof dans son application (il part dans les e-mails des cours privés) ; adresse, IBAN et AVS sont tenus par le prof dans son application."
          action={
            <span className="flex flex-wrap gap-2">
              <button className="btn-primary text-sm" onClick={() => setRemplacer(true)} data-testid="profs-remplacer"><ArrowRight size={14} /> Remplacer un prof</button>
              <button className="btn-ghost text-sm" onClick={() => setNouveau({ prenom: "", nom_complet: "", email: "" })} data-testid="profs-nouveau"><UserPlus size={14} /> Ajouter un prof</button>
            </span>
          }
          testid="profs-fiches">
          {nouveau && (
            <div className="bg-white rounded-2xl border p-4 flex flex-wrap items-end gap-2" style={{ borderColor: "var(--border)" }}>
              {[["prenom", "Prénom (comme au planning)"], ["nom_complet", "Nom complet"], ["email", "E-mail"]].map(([k, l]) => (
                <label key={k} className="text-xs flex-1 min-w-[160px]">{l}
                  <input className="input-field w-full mt-1" value={nouveau[k]} onChange={(e) => setNouveau({ ...nouveau, [k]: e.target.value })} />
                </label>
              ))}
              <button className="btn-primary text-sm" disabled={!nouveau.prenom.trim()} onClick={async () => {
                if (await essayer(() => api("/profs", { method: "POST", body: JSON.stringify(nouveau) }), `${nouveau.prenom} ajouté·e : son prénom est proposé dans le planning.`)) {
                  setNouveau(null); charger();
                }
              }}><Check size={14} /> Créer</button>
              <button className="btn-ghost text-sm" onClick={() => setNouveau(null)}>Annuler</button>
            </div>
          )}
          <Tableau
            entetes={["Prof", "Nom complet", "E-mail de connexion", "Téléphone", "Payé", "Tarif fixe", "Actif", ""]}
            lignes={d.profs}
            // « Corriger l'adresse » (À traiter › Adresses e-mail rejetées, 02.11.2026) : « fiches:<id> »
            // met la ligne du prof en jaune.
            surligne={vise?.vue === "fiches" && vise.ligne ? (p) => p.id === vise.ligne : undefined}
            rendu={(p) => (
              <>
                <Cellule fort>{p.prenom}{p.test && <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }}>compte de test</span>}
                  {(p.iban || p.adresse || p.avs) && (
                    <div className="text-[11px] font-normal text-[var(--muted)] mt-1">
                      {[[p.adresse, [p.npa, p.localite].filter(Boolean).join(" ")].filter(Boolean).join(", "),
                        p.iban ? `IBAN ${p.iban}` : "", p.avs ? `AVS ${p.avs}` : ""].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </Cellule>
                <Cellule>
                  <input className="input-field w-full" defaultValue={p.nom_complet} key={`n-${p.id}-${p.nom_complet}`}
                    onBlur={(e) => e.target.value.trim() !== (p.nom_complet || "") && enregistrer(p, { nom_complet: e.target.value.trim() })}
                    data-testid={`profs-nom-${p.id}`} />
                </Cellule>
                <Cellule>
                  <input className="input-field w-full" defaultValue={p.email} key={`e-${p.id}-${p.email}`} placeholder="adresse@…"
                    onBlur={(e) => e.target.value.trim().toLowerCase() !== (p.email || "") && enregistrer(p, { email: e.target.value })}
                    data-testid={`profs-email-${p.id}`} />
                </Cellule>
                {/* Le téléphone (Jen, 02.11.2026) : il part dans les e-mails des cours privés
                    (le prof du cours + Noémie). Le prof peut aussi le tenir dans son app. */}
                <Cellule>
                  <input className="input-field w-36" type="tel" defaultValue={p.telephone || ""} key={`tel-${p.id}-${p.telephone}`} placeholder="+41 79 …"
                    onBlur={(e) => enregistrerTel(p, e.target)} aria-invalid={!!telErreur[p.id]}
                    data-testid={`profs-telephone-${p.id}`} />
                  {telErreur[p.id] && (
                    <p className="text-xs mt-1 max-w-[16rem] leading-snug" style={{ color: "#B91C1C" }} role="alert"
                      data-testid={`profs-telephone-erreur-${p.id}`}>{telErreur[p.id]}</p>
                  )}
                </Cellule>
                <Cellule>
                  <input type="checkbox" checked={!!p.paye} onChange={(e) => enregistrer(p, { paye: e.target.checked })} />
                </Cellule>
                <Cellule>
                  <input className="input-field w-24" type="number" min="0" defaultValue={p.tarif_fixe ?? ""} key={`t-${p.id}-${p.tarif_fixe}`} placeholder="barème"
                    onBlur={(e) => String(p.tarif_fixe ?? "") !== e.target.value && enregistrer(p, { tarif_fixe: e.target.value ? Number(e.target.value) : null })} />
                  {p.tarif_fixe ? (
                    <select className="input-field w-24 mt-1 text-xs" value={p.tarif_fixe_par === "cours" ? "cours" : "heure"}
                      onChange={(e) => enregistrer(p, { tarif_fixe_par: e.target.value })} data-testid={`profs-tarif-par-${p.id}`}>
                      <option value="heure">/ heure</option>
                      <option value="cours">/ cours</option>
                    </select>
                  ) : null}
                </Cellule>
                <Cellule>
                  <input type="checkbox" checked={p.actif !== false} onChange={(e) => enregistrer(p, { actif: e.target.checked })} />
                </Cellule>
                <Cellule droite>
                  <button className="btn-ghost text-xs" onClick={() => apercu(p.id)} data-testid={`profs-apercu-${p.id}`}>
                    <ExternalLink size={12} /> Son application
                  </button>
                </Cellule>
              </>
            )}
          />
        </Section>
      )}

      {/* « À traiter » › « Horaires partagés 2027/2028 : … à préparer » ouvre l'écran sur la
          saison suivante (« horaires:2027-2028 »). */}
      {onglet === "horaires" && <HorairesPartages saisonInitiale={vise?.vue === "horaires" && /^\d{4}-\d{4}$/.test(vise.ligne || "") ? vise.ligne : ""} />}
      {/* Là où l'écran des horaires partagés est ouvert, il est LA référence, sans Excel
          (Jen, 31.10.2026) ; ailleurs (production, en attendant), la liste des dates importées. */}
      {onglet === "horaires" && !chantiers.horaires_partages && (
        <Section titre="Horaires des cours partagés (comédie musicale)"
          aide="Qui enseigne quand, date par date. Le décompte de chaque prof en dépend. Importer un fichier Excel (date, profs, horaire) remplace seulement les dates qu'il contient."
          action={
            <label className="btn-primary text-sm cursor-pointer inline-flex">
              <Upload size={14} /> Importer (.xlsx)
              <input type="file" accept=".xlsx" className="hidden" onChange={importer} />
            </label>
          }
          testid="profs-horaires">
          <Tableau
            entetes={["Date", "Cours", "Qui", "Horaire"]}
            lignes={horaires?.horaires || []}
            vide={horaires ? "Aucun horaire enregistré." : "Chargement…"}
            rendu={(h) => (
              <>
                <Cellule muet>{jour(h.date)}</Cellule>
                <Cellule muet>{(h.cours || []).join(", ") || "comédie musicale"}</Cellule>
                <Cellule fort>{h.ferme ? <span style={{ color: "#B45309" }}>Pas de cours</span> : (h.profs || []).map((x) => x.nom).join(", ")}</Cellule>
                <Cellule>{h.ferme ? (h.note || "") : (h.profs || []).map((x) => `${x.nom} ${x.debut}–${x.fin}`).join(" · ")}</Cellule>
              </>
            )}
          />
        </Section>
      )}
    </div>
  );
}
